import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanDeactivateFn,
  Router,
  UrlTree
} from '@angular/router';

import { scheduleAdditionsHref } from 'src/app/features/events/itinerary/schedule-additions/axle-schedule-additions.constants';
import { ScheduleEventPanelStore } from 'src/app/features/events/schedule/schedule-event-panel.state';
import { scheduleEventSchedulerNavigation } from 'src/app/features/events/schedule/schedule-event.constants';
import { ConfigureEventStore } from 'src/app/features/events/state/configure-events.state';
import { CreateScheduledEventStore } from 'src/app/features/events/state/create-scheduled-event.state';
import { SelectedScheduleEventDetailsStore } from 'src/app/features/events/state/selected-schedule-event.state';
import { UPDATE_EVENT_SCHEDULE_MODE_ABOUT_SECTION_QUERY_PARAM } from 'src/app/shared/components/modals/update-event-schedule-mode-modal/about/axle-update-event-schedule-mode-about.constants';
import { UpdateEventScheduleModeSaveService } from 'src/app/shared/components/modals/update-event-schedule-mode-modal/update-event-schedule-mode-save.service';
import { EventAllocationManagerStore } from 'src/app/shared/services/events/allocation-manager/allocation-manager.state';
import {
  isAllocationEventDetailsDto,
  isScheduledEventDetailsDto,
  toScheduledEventDetails
} from 'src/app/shared/services/events/events-payload.functions';
import { EventDetailsDto } from 'src/app/shared/services/events/events.models';
import {
  SelectedEventDetailsStore,
  SelectedEventOverviewStore
} from 'src/app/shared/services/events/events.state';
import { EventFeedbackStore } from 'src/app/shared/services/events/feedback/event-feedback.state';
import { SelectedEventGuestListStore } from 'src/app/shared/services/events/guests/event-guest-list.state';
import { EventHostManagementStore } from 'src/app/shared/services/events/host-management/event-host-management.state';
import {
  EventLevelItineraryStore,
  SelectedEventItineraryStore
} from 'src/app/shared/services/events/itinerary/event-itinerary.state';
import { EventPresentationStore } from 'src/app/shared/services/events/presentations/event-presentations.state';
import { EventRequestableAdditionsStore } from 'src/app/shared/services/events/requestable-additions/requestable-additions.state';
import { SelectedEventRequestsStore } from 'src/app/shared/services/events/requests/event-requests.state';
import { EventRoomStore } from 'src/app/shared/services/events/rooms/event-rooms.state';
import { EventScheduleAdditionsStore } from 'src/app/shared/services/events/schedule-additions/schedule-additions.state';
import { EventSchedulerStore } from 'src/app/shared/services/events/scheduler/event-scheduler.state';
import { EventTemplatesListRefreshService } from 'src/app/shared/services/events/templates/event-templates-list-refresh.service';
import { EventTemplate } from 'src/app/shared/services/events/templates/event-templates.models';
import { EventTemplateStore } from 'src/app/shared/services/events/templates/event-templates.state';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { QuestionnaireStore } from 'src/app/shared/services/questionnaire/state/questionnaire.state';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserStore } from 'src/app/shared/services/user/user.state';

export const confirmUpdateEventScheduleModeUnsavedChangesGuard: CanDeactivateFn<
  void
> = () => {
  const saveService = inject(UpdateEventScheduleModeSaveService);
  return saveService.confirmNavigationAway();
};

export const clearEventsStore: CanDeactivateFn<void> = () => {
  const selectedEventGuestListStore = inject(SelectedEventGuestListStore);
  const selectedEventHostManagementStore = inject(EventHostManagementStore);
  const selectedEventItineraryStore = inject(SelectedEventItineraryStore);
  const selectedEventLevelItineraryStore = inject(EventLevelItineraryStore);
  const selectedEventOverviewStore = inject(SelectedEventOverviewStore);
  const selectedEventPresentationStore = inject(EventPresentationStore);
  const selectedEventRequestsStore = inject(SelectedEventRequestsStore);
  const selectedEventRoomsStore = inject(EventRoomStore);
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const selectedEventAllocationManagerStore = inject(
    EventAllocationManagerStore
  );
  const selectedEventScheduleAdditionsStore = inject(
    EventScheduleAdditionsStore
  );
  const selectedEventRequestableAdditionsStore = inject(
    EventRequestableAdditionsStore
  );
  const questionnaireStore = inject(QuestionnaireStore);
  const feedbackStore = inject(EventFeedbackStore);

  selectedEventGuestListStore.reset();
  selectedEventHostManagementStore.reset();
  selectedEventItineraryStore.reset(false);
  selectedEventLevelItineraryStore.reset();
  selectedEventOverviewStore.reset();
  selectedEventPresentationStore.reset();
  selectedEventRequestsStore.reset();
  selectedEventRoomsStore.reset();
  selectedEventStore.reset();
  selectedEventAllocationManagerStore.reset();
  selectedEventScheduleAdditionsStore.reset();
  selectedEventRequestableAdditionsStore.reset();
  questionnaireStore.reset();
  feedbackStore.reset();
  inject(SelectedScheduleEventDetailsStore).reset();
  inject(ScheduleEventPanelStore).reset();
  inject(EventSchedulerStore).reset();

  return true;
};

export const clearScheduleEventStore: CanDeactivateFn<void> = () => {
  inject(SelectedScheduleEventDetailsStore).reset();
  inject(ScheduleEventPanelStore).reset();
  inject(EventSchedulerStore).reset();
  return true;
};

export const ensureScheduleEventDetailsLoadedGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const selectedScheduleEventStore = inject(SelectedScheduleEventDetailsStore);
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const router = inject(Router);
  const toast = inject(AxleToastService);

  const eventId = route.params['id'];

  if (!eventId) {
    router.navigate(['/events']);
    return false;
  }

  const details = selectedScheduleEventStore.details();

  if (details?.eventId === eventId) {
    return true;
  }

  inject(ScheduleEventPanelStore).reset();
  inject(EventSchedulerStore).reset();

  const redirectToAllocationEventDetails = (dto: EventDetailsDto) => {
    if (!isAllocationEventDetailsDto(dto)) {
      router.navigate(['/events']);
      return false;
    }

    return router.createUrlTree(['/events', dto.eventId]);
  };

  return new Promise<boolean | UrlTree>((resolve) => {
    let settled = false;
    const settle = (result: boolean | UrlTree) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };

    const timeoutId = setTimeout(() => {
      toast.addError('Failed to get event details');
      settle(router.parseUrl('/events'));
    }, 10000);

    selectedScheduleEventStore.fetchDetails({
      eventId,
      onSuccess: (dto) => {
        if (settled) return;

        if (!toScheduledEventDetails(dto)) {
          selectedEventStore.setDetailsFromDto(dto);
          settle(redirectToAllocationEventDetails(dto));
          return;
        }

        settle(true);
      },
      onFail: (err) => {
        if (settled) return;
        if (err?.errorCode !== 277) {
          toast.addError('Failed to get event details');
        }
        settle(router.parseUrl('/events'));
      }
    });
  });
};

export const ensureEventDetailsLoadedGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const selectedScheduleEventStore = inject(SelectedScheduleEventDetailsStore);
  const selectedOverviewStore = inject(SelectedEventOverviewStore);
  const configureEventStore = inject(ConfigureEventStore);
  const router = inject(Router);
  const toast = inject(AxleToastService);

  const eventId = route.params['id'];

  if (!eventId) {
    router.navigate(['/events']);
    return false;
  }

  const details = selectedEventStore.details();

  if (details && details.eventId === eventId) {
    selectedOverviewStore.fetchOverview({});
    configureEventStore.setEventDetails(details, 'UPDATE');
    return true;
  }

  const redirectToScheduleEventScheduler = (dto: EventDetailsDto) => {
    if (!isScheduledEventDetailsDto(dto)) {
      router.navigate(['/events']);
      return false;
    }

    return router.createUrlTree(scheduleEventSchedulerNavigation(dto.eventId));
  };

  return new Promise<boolean | UrlTree>((resolve) => {
    let settled = false;
    const settle = (result: boolean | UrlTree) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };

    const timeoutId = setTimeout(() => {
      toast.addError('Failed to get event details');
      settle(router.parseUrl('/events'));
    }, 10000);

    selectedEventStore.fetchDetails({
      eventId,
      onSuccess: (dto) => {
        if (settled) return;
        if (!isAllocationEventDetailsDto(dto)) {
          selectedScheduleEventStore.setDetailsFromDto(dto);
          settle(redirectToScheduleEventScheduler(dto));
          return;
        }

        selectedOverviewStore.fetchOverview({});
        configureEventStore.setEventDetails(dto, 'UPDATE');
        settle(true);
      },
      onFail: (err) => {
        if (settled) return;
        if (err?.errorCode !== 277) {
          toast.addError('Failed to get event details');
        }
        settle(router.parseUrl('/events'));
      }
    });
  });
};

export const syncEventDetailsGuard: CanActivateFn = () => {
  const configureEventStore = inject(ConfigureEventStore);
  const selectedEventStore = inject(SelectedEventDetailsStore);

  const details = selectedEventStore.details();
  if (details) {
    configureEventStore.setEventDetails(details, 'UPDATE');
  }
  return true;
};

export const canViewAllocationManagerGuard: CanActivateFn = () => {
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const router = inject(Router);

  if (!selectedEventStore.canViewAllocationManager()) {
    router.navigate(['/crm/contacts']);
    return false;
  }

  return true;
};

export const supportsRequestableAdditionsGuard: CanActivateFn = () => {
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const router = inject(Router);

  if (selectedEventStore.supportsRequestableAdditions()) return true;

  const eventId = selectedEventStore.eventId();
  if (!eventId) return router.parseUrl('/events');

  return router.parseUrl(scheduleAdditionsHref(eventId, 'schedule'));
};

export const canViewPresentationsGuard: CanActivateFn = () => {
  const selectedEventStore = inject(SelectedEventDetailsStore);
  const router = inject(Router);

  if (!selectedEventStore.canViewPresentations()) {
    router.navigate(['/crm/contacts']);
    return false;
  }

  return true;
};

export const stripUpdateEventScheduleModeModalForNonCoordinatorGuard: CanActivateFn =
  (route, state) => {
    if (route.queryParams['modal'] !== 'update_event_schedule_mode')
      return true;

    const userStore = inject(UserStore);
    if (userStore.canCoordinateScheduleEvents()) return true;

    const router = inject(Router);
    const urlTree = router.parseUrl(state.url);
    delete urlTree.queryParams['modal'];
    delete urlTree.queryParams[
      UPDATE_EVENT_SCHEDULE_MODE_ABOUT_SECTION_QUERY_PARAM
    ];
    return urlTree;
  };

export const canViewScheduleEventPresentationsGuard: CanActivateFn = () => {
  const selectedScheduleEventStore = inject(SelectedScheduleEventDetailsStore);
  const router = inject(Router);

  if (!selectedScheduleEventStore.canViewPresentations()) {
    const eventId = selectedScheduleEventStore.eventId();
    if (eventId) {
      return router.createUrlTree(scheduleEventSchedulerNavigation(eventId));
    }

    return router.createUrlTree(['/events']);
  }

  return true;
};

export const ensureEventTemplateLoadedGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const templateStore = inject(EventTemplateStore);
  const configureEventStore = inject(ConfigureEventStore);
  const createScheduledEventStore = inject(CreateScheduledEventStore);
  const modalService = inject(ModalService);
  const refreshService = inject(EventTemplatesListRefreshService);
  const router = inject(Router);
  const toast = inject(AxleToastService);

  const templateId = route.params['id'];
  if (!templateId) {
    router.navigate(['/events/templates']);
    return false;
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (result: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };

    const timeoutId = setTimeout(() => {
      toast.addError('Failed to load event template');
      router.navigate(['/events/templates']);
      settle(false);
    }, 10000);

    templateStore.getTemplateDetails({
      id: templateId,
      onSuccess: (template) => {
        if (settled) return;
        if (template.eventTemplateMetadata.eventMode !== 'SCHEDULE') {
          configureEventStore.setTemplateDetails(template);
          settle(true);
          return;
        }

        createScheduledEventStore.hydrateFromTemplate(template);

        void router.navigate(['/events/templates']).then(() => {
          modalService.openModal<
            { feature: 'TEMPLATE'; mode: 'UPDATE'; template: EventTemplate },
            { template: EventTemplate }
          >(
            'update_event_schedule_mode',
            { feature: 'TEMPLATE', mode: 'UPDATE', template },
            (result) => {
              if (result?.template) refreshService.requestRefresh();
            }
          );
        });

        settle(false);
      },
      onFail: () => {
        if (settled) return;
        toast.addError('Failed to load event template');
        router.navigate(['/events/templates']);
        settle(false);
      }
    });
  });
};
